import { useEffect, useState } from "react";
import { api, relTime } from "@/lib/api";
import { Eye, Pulse, MapPin, MagnifyingGlass } from "@phosphor-icons/react";

const TYPE_META = {
  duplicate: { label: "DUPLICATE", tone: "badge-warn" },
  network:   { label: "NETWORK", tone: "badge-info" },
  hotspot:   { label: "HOTSPOT", tone: "badge-danger" },
  anomaly:   { label: "ANOMALY", tone: "badge-danger" },
};

const OSINT_MAP = "https://static.prod-images.emergentagent.com/jobs/4545735e-77e3-490b-85fb-3bc856dbec8e/images/2363b00f350c042171179770774587cb8ad0f3e73528cbf014a1bed124e9cfe2.png";

export default function Intelligence() {
  const [signals, setSignals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [llmOpen, setLlmOpen] = useState(false);
  const load = () => api.get("/intel-signals").then(({ data }) => setSignals(data));
  useEffect(() => { load(); }, []);
  const ingest = async () => {
    setBusy(true);
    try { await api.post("/intel-signals/ingest"); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="intel-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">IntelliProbe v3.0 · OSINT · AI-augmented</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Intelligence Dashboard</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">Pattern recognition · network mapping · hotspot analysis · anomaly alerts</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="intel-llm" onClick={() => setLlmOpen(true)} className="span-btn">
            <Eye size={14}/> AI brand scan
          </button>
          <button data-testid="intel-ingest" disabled={busy} onClick={ingest} className="span-btn span-btn-ghost">
            <MagnifyingGlass size={14}/> {busy ? "Scanning…" : "Quick simulation"}
          </button>
        </div>
      </div>
      {llmOpen && <LlmScanModal onClose={() => setLlmOpen(false)} onDone={(data) => {
        setLlmOpen(false);
        load();
        if (data?.auto_cases?.length) {
          alert(`IntelliProbe Agent opened ${data.auto_cases.length} new case(s): ${data.auto_cases.map(c => c.case_code).join(", ")}`);
        }
      }} />}

      <div className="card-flat relative overflow-hidden h-[320px] bg-black">
        <img src={OSINT_MAP} alt="" className="absolute inset-0 w-full h-full object-cover opacity-95" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 grid grid-cols-4 gap-px bg-white/10 text-white">
          {[
            { k: "OPEN SIGNALS", v: signals.filter(s => s.status === "open").length },
            { k: "HIGH SEV.", v: signals.filter(s => s.severity === "high").length },
            { k: "TYPES", v: new Set(signals.map(s => s.type)).size },
            { k: "JURISDICTIONS", v: new Set(signals.map(s => s.location?.split(",").slice(-1)[0]?.trim()).filter(Boolean)).size },
          ].map(m => (
            <div key={m.k} className="bg-black/60 backdrop-blur-sm px-4 py-3">
              <div className="font-mono text-[9px] tracking-[0.22em] opacity-60">{m.k}</div>
              <div className="font-display text-[26px] font-bold mt-1 tnum">{m.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {signals.map(s => {
          const m = TYPE_META[s.type] || { label: s.type.toUpperCase(), tone: "" };
          return (
            <div key={s.signal_id} className="card-flat p-5" data-testid={`signal-${s.signal_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Pulse size={14} className={s.severity === "high" ? "text-[var(--span-danger)]" : "text-[var(--span-text-2)]"} weight="bold" />
                  <span className={`badge ${m.tone}`}>{m.label}</span>
                  <span className={`badge ${s.severity === "high" ? "badge-danger" : s.severity === "low" ? "badge-success" : "badge-warn"}`}>{s.severity}</span>
                </div>
                <span className="text-[10px] font-mono text-[var(--span-text-2)]">{relTime(s.detected_at)}</span>
              </div>
              <div className="font-display font-semibold text-[15px] mt-3">{s.title}</div>
              <div className="text-[12px] text-[var(--span-text-2)] mt-1">{s.description}</div>
              {s.location && <div className="text-[11px] text-[var(--span-text-2)] mt-3 flex items-center gap-1"><MapPin size={11}/> {s.location}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LlmScanModal({ onClose, onDone }) {
  const [f, setF] = useState({ brand: "", country: "IN", industry: "", count: 4, save: true, auto_open_case: false });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const submit = async (savePersist) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/intel-signals/llm-generate", { ...f, save: savePersist });
      if (savePersist) onDone(data); else setPreview(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "AI scan failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="card-flat w-full max-w-[680px] max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">AI Intelligence Scan · claude-sonnet-4-5</div>
            <div className="font-display font-bold text-[17px]">Synthesize OSINT signals for a brand</div>
          </div>
          <button onClick={onClose}>×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Brand</label>
              <input required value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} placeholder="e.g., Bajaj Auto" className="span-input" data-testid="llm-brand"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Country</label>
              <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="span-input">
                {["IN", "BD", "PK", "SG", "AE", "UK", "US"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Industry (optional)</label>
            <input value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} placeholder="e.g., Automotive, Pharmaceuticals" className="span-input"/>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Count</label>
            <input type="number" min={1} max={8} value={f.count} onChange={(e) => setF({ ...f, count: parseInt(e.target.value) || 4 })} className="span-input font-mono w-[120px]"/>
          </div>
          <label className="flex items-start gap-3 p-3 border border-[var(--span-border)] cursor-pointer hover:bg-[var(--span-bg)]" data-testid="llm-agent-toggle">
            <input
              type="checkbox"
              checked={f.auto_open_case}
              onChange={(e) => setF({ ...f, auto_open_case: e.target.checked })}
              className="mt-1"
            />
            <div>
              <div className="font-display font-medium text-[13px] flex items-center gap-2">
                <span className="badge badge-info">AGENT MODE</span> Auto-open cases for high-severity signals
              </div>
              <div className="text-[11px] text-[var(--span-text-2)] mt-1">
                IntelliProbe will create a real case for every <b>high</b>-severity signal — with case code,
                activity log entry, and a link back to the source signal. No human triage needed.
              </div>
            </div>
          </label>
          {err && <div className="text-[12px] text-[var(--span-danger)]">{String(err)}</div>}
          {preview && (
            <div className="space-y-2 mt-3 border-t border-[var(--span-border)] pt-3">
              <div className="label-eyebrow">Preview — {preview.engine}</div>
              {preview.signals.map((s, i) => (
                <div key={i} className="surface-2 p-3">
                  <div className="flex items-center gap-2"><span className="badge">{s.type}</span><span className="badge">{s.severity}</span></div>
                  <div className="font-display font-semibold text-[14px] mt-1">{s.title}</div>
                  <div className="text-[12px] text-[var(--span-text-2)]">{s.description}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-[var(--span-border)] flex justify-end gap-2">
          <button onClick={onClose} className="span-btn span-btn-ghost">Close</button>
          <button disabled={busy || !f.brand} onClick={() => submit(false)} className="span-btn span-btn-ghost" data-testid="llm-preview">{busy ? "Scanning…" : "Preview"}</button>
          <button disabled={busy || !f.brand} onClick={() => submit(true)} className="span-btn" data-testid="llm-save">{busy ? "Scanning…" : "Run & save signals"}</button>
        </div>
      </div>
    </div>
  );
}


